import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import Modal from '../Modal.jsx';
import FormField from '../FormField.jsx';
import Button from '../Button.jsx';
import CategoryCombobox from '../CategoryCombobox.jsx';
import IntercompanyConfigPanel from '../intercompany/IntercompanyConfigPanel.jsx';
import { useCategories } from '../../hooks/useCategories.js';
import { useEntityScope } from '../../hooks/useEntityScope.js';
import { useCreateEmployee, useUpdateEmployee } from '../../hooks/useEmployees.js';

const TODAY = new Date().toISOString().slice(0, 10);

// Mêmes slugs que le service backend (repli sur la racine « Frais de personnel »
// quand la sous-catégorie Qonto n'existe pas encore sur l'entité).
const SALARY_SLUGS = ['frais-de-personnel--salaires', 'frais-personnel'];
const URSSAF_SLUGS = ['impots-et-taxes--national-insurance', 'impots-et-taxes'];
const MUTUELLE_SLUGS = ['frais-de-personnel--mutuelle', 'frais-personnel'];

const URSSAF_RATE = 0.45;
const RETIREMENT_RATE = 0.10;
const MUTUELLE_CENTS = 6000;

function centsToEuros(cents) {
  if (cents == null) return '';
  return String(cents / 100);
}

function eurosToCents(str) {
  const v = parseFloat(str);
  return isNaN(v) ? null : Math.round(v * 100);
}

function findCategoryId(categories, slugs) {
  for (const slug of slugs) {
    const hit = categories.find((c) => c.slug === slug);
    if (hit) return hit.id;
  }
  return null;
}

let chargeKeySeq = 0;
function nextChargeKey() {
  chargeKeySeq += 1;
  return `chg-${chargeKeySeq}`;
}

function emptyCharge(overrides = {}) {
  return {
    key: nextChargeKey(),
    id: null,
    label: '',
    category_id: null,
    amount: '',
    day_of_month: 15,
    month_offset: 1,
    frequency: 'monthly',
    ...overrides,
  };
}

function defaultForm() {
  return {
    first_name: '',
    last_name: '',
    job_title: '',
    start_date: TODAY,
    end_date: '',
    net_salary: '',
    salary_day: 31,
    salary_category_id: null,
    charges: [],
    intercompany_config: null,
  };
}

function formFromEmployee(employee) {
  return {
    first_name: employee.first_name ?? '',
    last_name: employee.last_name ?? '',
    job_title: employee.job_title ?? '',
    start_date: employee.start_date ?? TODAY,
    end_date: employee.end_date ?? '',
    net_salary: centsToEuros(employee.net_salary_cents),
    salary_day: employee.salary_day ?? 31,
    salary_category_id: employee.salary_category_id ?? null,
    intercompany_config: employee.intercompany_config ?? null,
    charges: (employee.charges ?? []).map((c) =>
      emptyCharge({
        id: c.id,
        label: c.label ?? '',
        category_id: c.category_id ?? null,
        amount: centsToEuros(c.amount_cents),
        day_of_month: c.day_of_month ?? 15,
        month_offset: c.month_offset ?? 1,
        frequency: c.frequency ?? 'monthly',
      })
    ),
  };
}

const inputClass = (hasError) =>
  `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition-colors disabled:opacity-50 ${
    hasError ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'
  }`;

const SALARY_DAYS = [31, ...Array.from({ length: 30 }, (_, i) => i + 1)];

// Coût mensuel employeur saisi (net + charges ramenées au mois), pour l'aperçu
// de la refacturation — même règle que monthly_cost_cents côté backend.
function monthlyCostCents(form) {
  const net = eurosToCents(form.net_salary) ?? 0;
  return form.charges.reduce((sum, c) => {
    const cents = eurosToCents(c.amount) ?? 0;
    return sum + (c.frequency === 'quarterly' ? Math.round(cents / 3) : cents);
  }, net);
}

function salaryDayLabel(day) {
  return day === 31 ? 'Dernier jour du mois' : `Le ${day}`;
}

/**
 * Modal de création / édition d'une fiche employé.
 *
 * Props :
 *   open      — booléen
 *   onClose   — callback
 *   employee  — fiche employé (mode édition) | null (mode création)
 */
export default function EmployeeFormModal({ open, onClose, employee = null }) {
  const isEdit = Boolean(employee);

  const [form, setForm] = useState(() => (isEdit ? formFromEmployee(employee) : defaultForm()));
  const [errors, setErrors] = useState({});

  const { activeEntityId } = useEntityScope();
  const { data: categoriesData } = useCategories();
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : (categoriesData?.categories ?? categoriesData?.data ?? []);

  const defaultSalaryCategoryId = useMemo(
    () => findCategoryId(categories, SALARY_SLUGS),
    [categories]
  );

  const createMut = useCreateEmployee();
  const updateMut = useUpdateEmployee();
  const mutation = isEdit ? updateMut : createMut;

  const defaultApplied = useRef(false);

  useEffect(() => {
    if (open) {
      setForm(isEdit ? formFromEmployee(employee) : defaultForm());
      setErrors({});
      defaultApplied.current = false;
      mutation.reset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee?.id]);

  // Les catégories arrivent en asynchrone : la valeur par défaut ne peut être
  // posée qu'une fois, sinon elle réécrirait un choix explicite de l'utilisateur.
  useEffect(() => {
    if (!open || isEdit || defaultApplied.current || !defaultSalaryCategoryId) return;
    defaultApplied.current = true;
    setForm((f) => (f.salary_category_id ? f : { ...f, salary_category_id: defaultSalaryCategoryId }));
  }, [open, isEdit, defaultSalaryCategoryId]);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function setChargeField(index, key, value) {
    setForm((f) => ({
      ...f,
      charges: f.charges.map((c, i) => (i === index ? { ...c, [key]: value } : c)),
    }));
    setErrors((e) => {
      if (!e.charges) return e;
      const charges = e.charges.map((ce, i) => (i === index ? { ...ce, [key]: undefined } : ce));
      return { ...e, charges };
    });
  }

  function addCharge() {
    setForm((f) => ({ ...f, charges: [...f.charges, emptyCharge()] }));
  }

  function removeCharge(index) {
    setForm((f) => ({ ...f, charges: f.charges.filter((_, i) => i !== index) }));
    setErrors((e) => (e.charges ? { ...e, charges: e.charges.filter((_, i) => i !== index) } : e));
  }

  function prefillCharges() {
    const netCents = eurosToCents(form.net_salary);
    if (netCents == null || netCents <= 0) {
      setErrors((e) => ({ ...e, net_salary: 'Renseignez le salaire net avant de pré-remplir' }));
      return;
    }
    const urssafCategory = findCategoryId(categories, URSSAF_SLUGS);
    const mutuelleCategory = findCategoryId(categories, MUTUELLE_SLUGS);
    setForm((f) => ({
      ...f,
      charges: [
        emptyCharge({
          label: 'URSSAF',
          category_id: urssafCategory,
          amount: centsToEuros(Math.round(netCents * URSSAF_RATE)),
        }),
        emptyCharge({
          label: 'Retraite',
          category_id: mutuelleCategory,
          amount: centsToEuros(Math.round(netCents * RETIREMENT_RATE)),
        }),
        emptyCharge({
          label: 'Mutuelle',
          category_id: mutuelleCategory,
          amount: centsToEuros(MUTUELLE_CENTS),
        }),
      ],
    }));
    setErrors((e) => ({ ...e, charges: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Le prénom est requis';
    if (!form.last_name.trim()) e.last_name = 'Le nom est requis';
    if (!form.start_date) e.start_date = "La date d'entrée est requise";
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      e.end_date = "La sortie doit être postérieure ou égale à l'entrée";
    }
    const netCents = eurosToCents(form.net_salary);
    if (netCents == null || netCents <= 0) {
      e.net_salary = 'Le salaire net doit être supérieur à 0';
    }

    const chargeErrors = form.charges.map((c) => {
      const ce = {};
      if (!c.label.trim()) ce.label = 'Libellé requis';
      const cents = eurosToCents(c.amount);
      if (cents == null || cents <= 0) ce.amount = 'Montant > 0 requis';
      if (!c.category_id) ce.category_id = 'Catégorie requise';
      return ce;
    });
    if (chargeErrors.some((ce) => Object.keys(ce).length > 0)) e.charges = chargeErrors;

    return e;
  }

  function buildPayload() {
    return {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      job_title: form.job_title.trim() || null,
      start_date: form.start_date,
      end_date: form.end_date || null,
      net_salary_cents: eurosToCents(form.net_salary),
      salary_category_id: form.salary_category_id ?? null,
      salary_day: Number(form.salary_day),
      // Une seule config pour le salaire et ses charges : le backend la recopie
      // sur chaque récurrence générée par la fiche.
      intercompany_config: form.intercompany_config ?? null,
      // Remplacement complet côté API : une charge renvoyée avec son id garde
      // l'identité de sa récurrence (et donc sa config intercompany).
      charges: form.charges.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        label: c.label.trim(),
        category_id: c.category_id,
        amount_cents: eurosToCents(c.amount),
        day_of_month: Number(c.day_of_month),
        month_offset: Number(c.month_offset),
        frequency: c.frequency,
      })),
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = buildPayload();
    try {
      if (isEdit) {
        await mutation.mutateAsync({ id: employee.id, ...payload });
      } else {
        await mutation.mutateAsync(payload);
      }
      onClose?.();
    } catch (err) {
      setErrors({ submit: err.message ?? 'Une erreur est survenue' });
    }
  }

  const isPending = mutation.isPending;
  const chargeErrors = errors.charges ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Modifier la fiche employé' : 'Nouvel employé'}
      size="lg"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Prénom" htmlFor="employee-first-name" required error={errors.first_name}>
            <input
              id="employee-first-name"
              type="text"
              value={form.first_name}
              onChange={(e) => setField('first_name', e.target.value)}
              className={inputClass(errors.first_name)}
              disabled={isPending}
            />
          </FormField>

          <FormField label="Nom" htmlFor="employee-last-name" required error={errors.last_name}>
            <input
              id="employee-last-name"
              type="text"
              value={form.last_name}
              onChange={(e) => setField('last_name', e.target.value)}
              className={inputClass(errors.last_name)}
              disabled={isPending}
            />
          </FormField>
        </div>

        <FormField label="Poste" htmlFor="employee-job-title">
          <input
            id="employee-job-title"
            type="text"
            value={form.job_title}
            onChange={(e) => setField('job_title', e.target.value)}
            placeholder="Ex : Magasinier"
            className={inputClass()}
            disabled={isPending}
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField label="Date d'entrée" htmlFor="employee-start-date" required error={errors.start_date}>
            <input
              id="employee-start-date"
              type="date"
              value={form.start_date}
              onChange={(e) => setField('start_date', e.target.value)}
              className={inputClass(errors.start_date)}
              disabled={isPending}
            />
          </FormField>

          <FormField label="Date de sortie" htmlFor="employee-end-date" error={errors.end_date}>
            <input
              id="employee-end-date"
              type="date"
              value={form.end_date}
              onChange={(e) => setField('end_date', e.target.value)}
              className={inputClass(errors.end_date)}
              disabled={isPending}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FormField
            label="Salaire net mensuel (€)"
            htmlFor="employee-net-salary"
            required
            error={errors.net_salary}
          >
            <input
              id="employee-net-salary"
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={form.net_salary}
              onChange={(e) => setField('net_salary', e.target.value)}
              placeholder="0,00"
              className={inputClass(errors.net_salary)}
              disabled={isPending}
            />
          </FormField>

          <FormField label="Jour de paie" htmlFor="employee-salary-day">
            <select
              id="employee-salary-day"
              value={form.salary_day}
              onChange={(e) => setField('salary_day', Number(e.target.value))}
              className={inputClass()}
              disabled={isPending}
            >
              {SALARY_DAYS.map((d) => (
                <option key={d} value={d}>{salaryDayLabel(d)}</option>
              ))}
            </select>
          </FormField>
        </div>

        <FormField label="Catégorie du salaire">
          <CategoryCombobox
            value={form.salary_category_id}
            onChange={(id) => setField('salary_category_id', id)}
            placeholder="Catégorie du salaire"
          />
        </FormField>

        {/* ── Charges patronales / annexes ── */}
        <div className="space-y-3 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="flex-1 text-sm font-semibold text-slate-800">Charges</h3>
            <button
              type="button"
              onClick={prefillCharges}
              disabled={isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 min-h-[44px] md:min-h-0 md:py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
            >
              <Wand2 size={14} aria-hidden /> Pré-remplir les charges
            </button>
            <button
              type="button"
              onClick={addCharge}
              disabled={isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 min-h-[44px] md:min-h-0 md:py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50"
            >
              <Plus size={14} aria-hidden /> Ajouter une charge
            </button>
          </div>

          {form.charges.length === 0 && (
            <p className="text-xs text-slate-500">
              Aucune charge — le prévisionnel ne portera que le salaire net.
            </p>
          )}

          {form.charges.map((charge, index) => {
            const ce = chargeErrors[index] ?? {};
            return (
              <div
                key={charge.key}
                className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <FormField label="Libellé" htmlFor={`charge-label-${index}`} error={ce.label}>
                      <input
                        id={`charge-label-${index}`}
                        type="text"
                        value={charge.label}
                        onChange={(e) => setChargeField(index, 'label', e.target.value)}
                        placeholder="Ex : URSSAF"
                        className={inputClass(ce.label)}
                        disabled={isPending}
                      />
                    </FormField>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCharge(index)}
                    aria-label={`Supprimer la charge ${index + 1}`}
                    className="mt-6 shrink-0 inline-flex items-center justify-center rounded-lg p-2 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                    disabled={isPending}
                  >
                    <Trash2 size={16} aria-hidden />
                  </button>
                </div>

                <FormField label="Catégorie" error={ce.category_id}>
                  <CategoryCombobox
                    value={charge.category_id}
                    onChange={(id) => setChargeField(index, 'category_id', id)}
                    placeholder="Catégorie de la charge"
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormField label="Montant (€)" htmlFor={`charge-amount-${index}`} error={ce.amount}>
                    <input
                      id={`charge-amount-${index}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      inputMode="decimal"
                      value={charge.amount}
                      onChange={(e) => setChargeField(index, 'amount', e.target.value)}
                      placeholder="0,00"
                      className={inputClass(ce.amount)}
                      disabled={isPending}
                    />
                  </FormField>

                  <FormField label="Jour" htmlFor={`charge-day-${index}`}>
                    <select
                      id={`charge-day-${index}`}
                      value={charge.day_of_month}
                      onChange={(e) => setChargeField(index, 'day_of_month', Number(e.target.value))}
                      className={inputClass()}
                      disabled={isPending}
                    >
                      {SALARY_DAYS.map((d) => (
                        <option key={d} value={d}>{salaryDayLabel(d)}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField
                    label="Décalage (mois)"
                    htmlFor={`charge-offset-${index}`}
                    hint="0 = le mois du salaire"
                  >
                    <select
                      id={`charge-offset-${index}`}
                      value={charge.month_offset}
                      onChange={(e) => setChargeField(index, 'month_offset', Number(e.target.value))}
                      className={inputClass()}
                      disabled={isPending}
                    >
                      {[0, 1, 2, 3].map((o) => (
                        <option key={o} value={o}>{o === 0 ? 'Même mois' : `M+${o}`}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Fréquence" htmlFor={`charge-frequency-${index}`}>
                    <select
                      id={`charge-frequency-${index}`}
                      value={charge.frequency}
                      onChange={(e) => setChargeField(index, 'frequency', e.target.value)}
                      className={inputClass()}
                      disabled={isPending}
                    >
                      <option value="monthly">Mensuel</option>
                      <option value="quarterly">Trimestriel</option>
                    </select>
                  </FormField>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Refacturation intra-groupe (salaire + charges, en bloc) ── */}
        {activeEntityId && (
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <h3 className="text-sm font-semibold text-slate-800">Refacturation intra-groupe</h3>
            <p className="text-xs text-slate-500">
              S'applique au salaire net et à toutes les charges de la fiche.
            </p>
            <IntercompanyConfigPanel
              value={form.intercompany_config}
              onChange={(cfg) => setField('intercompany_config', cfg)}
              fromEntityId={activeEntityId}
              amountCents={monthlyCostCents(form)}
              sourceVatRate={0}
            />
          </div>
        )}

        {errors.submit && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {errors.submit}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending} className="flex-1">
            Annuler
          </Button>
          <Button type="submit" loading={isPending} disabled={isPending} className="flex-1">
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
